import {
  AutocompleteWithLabel,
  CheckboxWithLabel,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { MenuItem, Typography } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { WALLET_TYPES_QUERY } from '../../graphql/walletTypes'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Gateway } from '../../types/gateway'
import { ProviderBrief } from '../../types/provider'
import { WalletType } from '../../types/wallet'
import useStyles from './styles/GatewayBasicInfo.styles'

interface GatewayBasicInfoProps {
  values: Gateway
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<Gateway>
  touched: FormikTouched<Gateway>
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  isSubmitting: boolean
  setNameForBreadcrumbs: React.Dispatch<React.SetStateAction<string>>
}

const GatewayBasicInfo: React.FC<GatewayBasicInfoProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  setNameForBreadcrumbs
}) => {
  const classes = useStyles()

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const { data: walletTypeList, loading: loadingWalletTypes } = useQuery<{
    walletTypes: WalletType[]
  }>(WALLET_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const foundProvider = providerData?.providers.find(
    (p) => p.displayProviderCode === values.basicDetails.displayEntityCode
  )

  const [autoCompleteLabelForEntity, setautoCompleteLabelForEntity] =
    useState<any>({
      value: values.basicDetails.entityId,
      label:
        lang === 'fa'
          ? `${foundProvider?.nameTranslatedList[0].value} - (${foundProvider?.displayProviderCode})`
          : `${foundProvider?.name} - (${foundProvider?.displayProviderCode})`
    })

  const [autoCompleteLabelForWalletType, setAutoCompleteLabelForWalletType] =
    useState<any>({
      value: values.basicDetails.walletType,
      label: values.basicDetails.walletType
    })

  useEffect(() => {
    if (loadingForProvider || !values.basicDetails.entityId) {
      return
    }
    setautoCompleteLabelForEntity({
      value: values.basicDetails.entityId,
      label:
        lang === 'fa'
          ? `${foundProvider?.nameTranslatedList[0].value} - (${foundProvider?.displayProviderCode})`
          : `${foundProvider?.name} - (${foundProvider?.displayProviderCode})`
    })
    setAutoCompleteLabelForWalletType({
      value: values.basicDetails.walletType,
      label: values.basicDetails.walletType
    })
  }, [
    values.basicDetails.entityId,
    loadingForProvider,
    values.basicDetails.walletType
  ])

  const [autoCompleteLabelCurrencies, setautoCompleteLabelCurrencies] =
    useState<any[]>([])

  useEffect(() => {
    const temp: any[] = []
    values?.basicDetails?.supportedCurrencies.forEach((value) => {
      temp.push({ label: value?.toString(), value: value?.toString() })
    })
    setautoCompleteLabelCurrencies(temp)
  }, [values.basicDetails?.supportedCurrencies])

  useEffect(() => {
    setNameForBreadcrumbs(values.basicDetails.name)
  }, [values.basicDetails.name])

  return (
    <div>
      <PageHeader title={values.basicDetails.name} />
      <SectionTitle title="Basic Details" marginBottom />
      <div className={classes.inputsContainer}>
        <InputWithLabel
          inputSize="small"
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the Name')}
          value={values.basicDetails.name}
          name="basicDetails.name"
          id="basicDetails.name"
          onChange={(event) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
        />
        <SelectWithLabel
          label={getTranslate('Entity Type')}
          placeholder={getTranslate('Select the Entity Type')}
          name="basicDetails.entityType"
          fullWidth
          value={values.basicDetails.entityType}
          onChange={(event: { target: { value: any } }) =>
            setFieldValue('basicDetails.entityType', event.target.value)
          }
          size="small"
          error={
            (touched.basicDetails?.entityType &&
              !!errors.basicDetails?.entityType) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.entityType
              ? getTranslate(errors.basicDetails?.entityType || '')
              : undefined
          }>
          <MenuItem key="Provider" value="Provider">
            {getTranslate('Provider')}
          </MenuItem>
        </SelectWithLabel>
        <AutocompleteWithLabel
          name="basicDetails.entityId"
          inputSize="small"
          label={getTranslate('Entity')}
          placeholder={getTranslate('Select the Entity Type')}
          includeInputInList
          onChange={(ـ, v: any) => {
            setautoCompleteLabelForEntity({ value: v.value, label: v.label })
            setFieldValue('basicDetails.entityId', v.value)
            setFieldValue('basicDetails.entity', v.label)
            setFieldValue('basicDetails.entityCode', v.code)
          }}
          getOptionSelected={(option, value) =>
            option.value === value.value && option.label === value.label
          }
          getOptionLabel={(option) => option.label || ''}
          options={
            providerData?.providers
              ? providerData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.id,
                  code: p.providerCode
                }))
              : []
          }
          renderOption={(option) => <>{option.label}</>}
          size="small"
          value={
            autoCompleteLabelForEntity && autoCompleteLabelForEntity.value
              ? autoCompleteLabelForEntity
              : null
          }
          error={
            (touched.basicDetails?.entityId &&
              !!errors.basicDetails?.entityId) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.entityId
              ? getTranslate(errors.basicDetails?.entityId || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          label={getTranslate('Supported Currencies')}
          placeholder={getTranslate('Select the supported surrencie')}
          name="basicDetails.supportedCurrencies"
          value={autoCompleteLabelCurrencies}
          onChange={(event, v: any) => {
            const selectedCurrencies: string[] = []
            v.map((value: any) => selectedCurrencies.push(value.value))
            setFieldValue(
              'basicDetails.supportedCurrencies',
              selectedCurrencies
            )
          }}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            getCurrenciesAvailable()
              ? getCurrenciesAvailable().map((item) => ({
                  label: item as string,
                  value: item as string
                }))
              : [{ label: 'nothing found', value: '' }]
          }
          size="small"
          multiple
          includeInputInList
          error={
            (touched.basicDetails?.supportedCurrencies &&
              !!errors.basicDetails?.supportedCurrencies) ||
            undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <AutocompleteWithLabel
          label={getTranslate('Wallet Type')}
          placeholder={getTranslate('Please Select Wallet Type')}
          value={autoCompleteLabelForWalletType}
          onChange={(_, value: any) => {
            if (value) {
              setFieldValue('basicDetails.walletType', value.value)
              setAutoCompleteLabelForWalletType({
                value: value.value,
                label: value.label
              })
            }
          }}
          options={
            walletTypeList?.walletTypes
              ? walletTypeList?.walletTypes.map((wt) => {
                  return {
                    value: wt.walletType,
                    label: getTranslate(wt.walletType)
                  }
                })
              : []
          }
          disabled={isSubmitting}
          size="small"
          inputSize="small"
          error={
            (touched.basicDetails?.walletType &&
              !!errors.basicDetails?.walletType) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.walletType
              ? getTranslate(errors.basicDetails?.walletType || '')
              : undefined
          }
          loading={loadingWalletTypes}
          loadingText={getTranslate('Loading') + '...'}
        />
        <div className={classes.mainCheckBox}>
          <CheckboxWithLabel
            inputSize="small"
            checked={values.basicDetails.active}
            onChange={(e) =>
              setFieldValue('basicDetails.active', e.currentTarget.checked)
            }
          />
          <Typography>{getTranslate('Active')}</Typography>
        </div>
        <div className={classes.description}>
          <TextAreaWithLabel
            label={getTranslate('Description')}
            placeholder={getTranslate('Enter the description')}
            autoComplete="off"
            name="basicDetails.description"
            value={values.basicDetails.description}
            onChange={(event: { currentTarget: { value: any } }) =>
              setFieldValue(
                'basicDetails.description',
                event.currentTarget.value
              )
            }
            error={
              (touched.basicDetails?.description &&
                !!errors.basicDetails?.description) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.description
                ? getTranslate(errors.basicDetails?.description || '')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rows={7}
          />
        </div>
      </div>
    </div>
  )
}
export default GatewayBasicInfo
